import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  ProductCatalogItem,
  SelectedProduct,
} from 'src/types/analytics-genesis';
import * as reduxApi from 'src/utils/redux-api';
import {thunkify as flow} from 'src/utils/thunks';
import {fetching, key} from 'src/utils/redux';


export const RECEIVE_PRODUCT_CATALOG_LIST = 'product-catalog-list/receive';
export const RECEIVE_CURRENT_PRODUCT_LIST_ITEM =
  'current-product-list-item/receive';

type ReceiveProductCatalogList = {
  type: 'product-catalog-list/receive';
  payload: {
    productCatalogList: Array<ProductCatalogItem>;
  };
};

type ReceiveCurrentProductListItem = {
  type: 'current-product-list-item/receive';
  payload: {
    currentProductListItem: SelectedProduct;
  };
};

export type AnalyticsGenesisActions =
  | ReceiveProductCatalogList
  | ReceiveCurrentProductListItem;

const receiveProductCatalogList = (payload: {
  productCatalogList: Array<ProductCatalogItem>;
}): ReceiveProductCatalogList => ({
  type: RECEIVE_PRODUCT_CATALOG_LIST,
  payload,
});

const receiveCurrentProductListItem = (payload: {
  currentProductListItem: SelectedProduct;
}): ReceiveCurrentProductListItem => ({
  type: RECEIVE_CURRENT_PRODUCT_LIST_ITEM,
  payload,
});

export const getCatalogList: (arg1: {
  getCustomTRMReport: boolean;
}) => ThunkAction<unknown> = flow(
  key(() => `getCatalogProductList`),
  fetching(),
)(({getCustomTRMReport}) => async (dispatch: Dispatch) => {
  try {
    // 1. Fetch product dashboard list
    const productDashboardList = await dispatch(
      reduxApi.get(`analytics/product-dashboards`, {}, {apiPath: '/api/v3/'}),
    );

    // 2. First dispatch the product list without TRM data
    // @ts-expect-error - TS18046 - 'productDashboardList' is of type 'unknown'.
    let finalProductList = productDashboardList.products ?? [];
    dispatch(
      receiveProductCatalogList({
        productCatalogList: finalProductList,
      }),
    );

    // 3. Try to fetch and merge TRM data

    if (getCustomTRMReport) {
      try {
        const trmData = await dispatch(
          reduxApi.get(`/api/reports/custom-reports`, {}, {apiPath: '/trmapi'}),
        );

        // @ts-expect-error - TS18046 - 'trmData' is of type 'unknown'. | TS7006 - Parameter 'item' implicitly has an 'any' type.
        const trmCustomReports = trmData.map((item) => ({
          name: item.name,
          URL: `/trm#/report-analytics/custom-reports/${item.id}`,
          icon: 'file-chart-column',
          isCustomReport: true,
        }));

        if (finalProductList.length > 0) {
          const hasCRM = finalProductList.some(
            // @ts-expect-error - TS7006 - Parameter 'product' implicitly has an 'any' type.
            (product) => product.name?.toLowerCase() === 'crm',
          );

          if (hasCRM) {
            // @ts-expect-error - TS7006 - Parameter 'product' implicitly has an 'any' type.
            finalProductList = finalProductList.map((product) => {
              if (product.name?.toLowerCase() === 'crm') {
                return {
                  ...product,
                  dashboards: [
                    ...(product.dashboards || []),
                    ...trmCustomReports,
                  ],
                };
              }
              return product;
            });
          } else {
            finalProductList = [
              ...finalProductList,
              {
                name: 'CRM',
                icon: 'screen-users',
                dashboards: trmCustomReports,
                isCustomReport: true,
              },
            ];
          }

          // 4. Dispatch updated list with TRM data
          dispatch(
            receiveProductCatalogList({
              productCatalogList: finalProductList,
            }),
          );
        }
      } catch (trmError: any) {
        console.error('Failed to fetch TRM data:', trmError);
        // The product list is already dispatched, so we can continue
      }
    }
  } catch (error: any) {
    console.error('Failed to fetch product dashboard list:', error);
  }
});

export const setCurrentProductListItem: (
  currentProductListItem: SelectedProduct,
) => ThunkAction<unknown> = (currentProductListItem) => (dispatch: Dispatch) =>
  dispatch(receiveCurrentProductListItem({currentProductListItem}));
